import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NodeEnv } from '../../../constants/enums';
import { readEnvVariable, readEnvVariableAsList } from '../../../utils';
import { GenericMap } from '../../../utils/types';
import {
  DEFAULT_AGENT_RESPONSE_TIME_LIMIT_SECONDS,
  DEFAULT_ORDER_CHECK_TIMEOUT_SECONDS,
} from './config.flags';

export interface ConfigState {
  NODE_ENV: NodeEnv;
  ITEM_BY_ITEM: boolean;
  DISABLE_INTENT_INTERVAL: number;
  FEATUREFLAG_TASKROUTER: boolean;
  TASKROUTER_WAITING_TIME: number;
  FEATURE_FLAG_MENU_VERSION: boolean;
  TASKROUTER_NOTIFICATION_SOUND: boolean;
  TASK_ROUTER_AGENT_AUTO_ASSIGNMENT: boolean;
  WEBSOCKET_STAGE_URL: string;
  WEBSOCKET_STAGE_BASIC_AUTH_PARAM: string;
  WEBSOCKET_URL: string;
  WEBSOCKET_BASIC_AUTH_PARAM: string;
  WEBSCOCKET_URL_INCLUDE_AUTH_PARAM: boolean;
  PRP_PERSISTENT_API: string;
  PRP_API: string;
  MENU_API: string;
  RESTAURANT_DIAGNOSTIC_API: string;
  PAGERDUTY_INTEGRATION_KEY: string;
  SESSION_BUFFER_API: string;
  SESSION_BUFFER_API_AUTHENTICATION: string;
  AUTH_URL: string;
  RESTAURANT_SERVICE_URL: string;
  EVENT_API: string;
  EVENT_API_BASIC_AUTH_PARAM: string;
  // Per-restaurant task-router registry. Answers "is this restaurant running on
  // the task router?" so direct-HITL (dropdown) agents can be warned that an
  // order may be routed to someone else at any moment. Basic auth — the
  // task-router service does not accept the HITL JWT.
  TASK_ROUTER_STATUS_API: string;
  TASK_ROUTER_STATUS_API_AUTH: string;
  WEBSOCKET_RETRY_MAX_BACKOFF: number;
  AGENT_HEARTBEAT_EVENT_INTERVAL: number;
  ALERT_TIMEOUT: number;
  FEATURE_FLAG_AUDIO_BACKEND: boolean;
  FINISH_ORDER_TIMER: number;
  AGENT_INACTIVITY_TIMER: number;
  ORDER_SUBMISSION_ALERT_TIMEOUT: number;
  FEATURE_FLAG_AUTO_COMBO: boolean;
  FEATURE_FLAG_BM_AUTO_COMBO: boolean;
  FEATURE_FLAG_LOYALTY_SUPPORT: boolean;
  MU_MENU_API: string;
  MU_MENU_API_TOKEN: string;
  UNIFIED_MENU_RESTAURANTS?: Array<string>;
  CONSOLIDATED_MENU_BRANDS: Array<string>;
  CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS: Array<string>;
  AI_ESCALATION_UPSELL: boolean;
  IDLE_LOGOUT_TIME: number;
  IPIFY_API: string;
  GROUPS_WITH_WHITELIST_IP: GenericMap<Array<string>>;
  FEATURE_FLAG_FINAL_ORDER_VALIDATION: boolean;
  FINAL_ORDER_PENDING_TIMEOUT: number;
  FINAL_ORDER_COMPLETED_TIMEOUT: number;
  FEATURE_FLAG_POS_CART: boolean;
  FEATURE_FLAG_ITEM_CONVERSION: boolean;
  FEATURE_FLAG_OCB_READBACK: boolean;
  FEATURE_FLAG_NOTIFY_HITL_BUILD: boolean;
  FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION: boolean;
  FINISH_ORDER_BUTTON_DISABLE_INTERVAL: number;
  FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES: boolean;
  FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE: boolean;
  FEATURE_FLAG_SUPPRESS_UPSELL: boolean;
  FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE: boolean;
  FEATURE_FLAG_UNRESOLVED_ITEMS: boolean;
  FEATURE_FLAG_HITL_3_0: boolean;
  FEATURE_FLAG_AGENT_CART: boolean;
  FEATURE_FLAG_HITL_EXPRESS_TIMER: boolean;
  HITL_EXPRESS_TIMER_MAX_SECONDS: number;
  // HITL 3.0: seconds within which the agent must respond (send a TTS) to the
  // customer. Drives the Pulse "Response time" tile — both the SLA threshold and
  // the number of bars. Sourced from the `agent_response_time` Unleash variant
  // (see useUnleash.hooks); this default applies until that resolves.
  AGENT_RESPONSE_TIME_LIMIT_SECONDS: number;
  FEATURE_FLAG_MENU_MODULE_ERROR_UI: boolean;
  FEATURE_FLAG_ORDER_CHECK_RETRY: boolean;
  // Seconds a non-final order may wait for its POSMON check event before the
  // Retry button is offered. Sourced from the `order_check_retry` Unleash
  // variant (see useUnleash.hooks); this default applies until that resolves.
  ORDER_CHECK_TIMEOUT_SECONDS: number;
  FEATURE_FLAG_MASK_ORDER_PENDING_ERROR: boolean;
  // Gates the secondary-ASR transcript path (HITL-1497). Read via
  // selectFeatureFlagSecondaryAsrEnabled.
  FEATURE_FLAG_SECONDARY_ASR_ENABLED: boolean;
  // HITL-1492 Pillar 3: in-screen issue reporting. Read via
  // selectIsIssueReportingEnabled, which ANDs this with FEATURE_FLAG_HITL_3_0.
  // The taxonomy itself is NOT config — it is hardcoded in
  // src/constants/issueTypes.ts (HITL-1501). This flag is on/off only.
  FEATURE_FLAG_ISSUE_REPORTING: boolean;
}

export const initialState: ConfigState = {
  NODE_ENV:
    process.env.REACT_APP_DEPLOY_ENV?.toUpperCase() ||
    (process.env.NODE_ENV.toUpperCase() as any),
  ITEM_BY_ITEM:
    (process.env.REACT_APP_ITEM_BY_ITEM
      ? process.env.REACT_APP_ITEM_BY_ITEM
      : 'FALSE'
    ).toUpperCase() === 'TRUE',
  DISABLE_INTENT_INTERVAL:
    parseInt(readEnvVariable('DISABLE_INTENT_INTERVAL')) || 3000, // in milliseconds,
  FEATUREFLAG_TASKROUTER:
    (process.env.REACT_APP_FEATUREFLAG_TASKROUTER || 'FALSE').toUpperCase() ===
    'TRUE',
  TASKROUTER_NOTIFICATION_SOUND:
    (
      process.env.REACT_APP_TASKROUTER_NOTIFICATION_SOUND || 'TRUE'
    ).toUpperCase() === 'TRUE',
  TASKROUTER_WAITING_TIME: process.env.REACT_APP_TASKROUTER_WAITING_TIME
    ? parseInt(process.env.REACT_APP_TASKROUTER_WAITING_TIME)
    : 5000,
  FEATURE_FLAG_MENU_VERSION:
    readEnvVariable('FEATURE_FLAG_MENU_VERSION').toUpperCase() === 'TRUE',
  WEBSOCKET_STAGE_URL: readEnvVariable('WEBSOCKET_STAGE_URL') || '',
  WEBSOCKET_STAGE_BASIC_AUTH_PARAM:
    readEnvVariable('WEBSOCKET_STAGE_BASIC_AUTH_PARAM') || '',
  WEBSOCKET_URL: readEnvVariable('WEBSOCKET_URL') || '',
  WEBSOCKET_BASIC_AUTH_PARAM:
    readEnvVariable('WEBSOCKET_BASIC_AUTH_PARAM') || '',
  WEBSCOCKET_URL_INCLUDE_AUTH_PARAM:
    readEnvVariable('WEBSCOCKET_URL_INCLUDE_AUTH_PARAM').toUpperCase() ===
    'TRUE',
  PRP_PERSISTENT_API: readEnvVariable('PRP_PERSISTENT_API') || '',
  PRP_API: readEnvVariable('PRP_API') || '',
  MENU_API: readEnvVariable('MENU_API') || '',
  RESTAURANT_DIAGNOSTIC_API: readEnvVariable('RESTAURANT_DIAGNOSTIC_API') || '',
  PAGERDUTY_INTEGRATION_KEY: readEnvVariable('PAGERDUTY_INTEGRATION_KEY') || '',
  SESSION_BUFFER_API: readEnvVariable('SESSION_BUFFER_API') || '',
  SESSION_BUFFER_API_AUTHENTICATION:
    readEnvVariable('SESSION_BUFFER_API_AUTHENTICATION') || '',
  AUTH_URL: readEnvVariable('AUTH_URL') || '',
  RESTAURANT_SERVICE_URL: readEnvVariable('RESTAURANT_SERVICE_URL') || '',
  TASK_ROUTER_AGENT_AUTO_ASSIGNMENT:
    readEnvVariable('TASK_ROUTER_AGENT_AUTO_ASSIGNMENT').toUpperCase() ===
    'TRUE',
  EVENT_API: readEnvVariable('EVENT_API') || '',
  EVENT_API_BASIC_AUTH_PARAM:
    readEnvVariable('EVENT_API_BASIC_AUTH_PARAM') || '',
  TASK_ROUTER_STATUS_API: readEnvVariable('TASK_ROUTER_STATUS_API') || '',
  TASK_ROUTER_STATUS_API_AUTH:
    readEnvVariable('TASK_ROUTER_STATUS_API_AUTH') || '',
  WEBSOCKET_RETRY_MAX_BACKOFF: process.env.REACT_APP_WEBSOCKET_RETRY_MAX_BACKOFF
    ? parseInt(process.env.REACT_APP_WEBSOCKET_RETRY_MAX_BACKOFF)
    : 10000,
  AGENT_HEARTBEAT_EVENT_INTERVAL:
    parseInt(readEnvVariable('AGENT_HEARTBEAT_EVENT_INTERVAL')) || 5000,
  ALERT_TIMEOUT: process.env.REACT_APP_ALERT_TIMEOUT
    ? parseInt(process.env.REACT_APP_ALERT_TIMEOUT)
    : 5000,
  FEATURE_FLAG_AUDIO_BACKEND: false,
  FINISH_ORDER_TIMER: parseInt(readEnvVariable('FINISH_ORDER_TIMER')) || 9,
  AGENT_INACTIVITY_TIMER:
    parseInt(readEnvVariable('AGENT_INACTIVITY_TIMER')) || 15,
  ORDER_SUBMISSION_ALERT_TIMEOUT: process.env
    .REACT_APP_ORDER_SUBMISSION_ALERT_TIMEOUT
    ? parseInt(process.env.REACT_APP_ORDER_SUBMISSION_ALERT_TIMEOUT)
    : 3000,
  FEATURE_FLAG_AUTO_COMBO: false,
  FEATURE_FLAG_BM_AUTO_COMBO: false,
  FEATURE_FLAG_LOYALTY_SUPPORT: false,
  MU_MENU_API: readEnvVariable('MU_MENU_API') || '',
  MU_MENU_API_TOKEN: readEnvVariable('MU_MENU_API_TOKEN') || '',
  UNIFIED_MENU_RESTAURANTS: [],
  CONSOLIDATED_MENU_BRANDS: readEnvVariableAsList('CONSOLIDATED_MENU_BRANDS'),
  CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS: readEnvVariableAsList(
    'CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS'
  ),
  AI_ESCALATION_UPSELL:
    readEnvVariable('AI_ESCALATION_UPSELL').toUpperCase() === 'TRUE',
  IDLE_LOGOUT_TIME:
    parseInt(readEnvVariable('IDLE_LOGOUT_TIME')) || 1000 * 60 * 60, // 60 MIN,
  IPIFY_API: readEnvVariable('IPIFY_API') || '',
  GROUPS_WITH_WHITELIST_IP: JSON.parse(
    readEnvVariable('GROUPS_WITH_WHITELIST_IP') || '{}'
  ),
  FEATURE_FLAG_FINAL_ORDER_VALIDATION: false,
  FINAL_ORDER_PENDING_TIMEOUT: process.env.REACT_APP_FINAL_ORDER_PENDING_TIMEOUT
    ? parseInt(process.env.REACT_APP_FINAL_ORDER_PENDING_TIMEOUT)
    : 10000, // 10 seconds default
  FINAL_ORDER_COMPLETED_TIMEOUT: process.env
    .REACT_APP_FINAL_ORDER_COMPLETED_TIMEOUT
    ? parseInt(process.env.REACT_APP_FINAL_ORDER_COMPLETED_TIMEOUT)
    : 3000, // 3 seconds default
  FEATURE_FLAG_POS_CART: false,
  FEATURE_FLAG_ITEM_CONVERSION:
    readEnvVariable('FEATURE_FLAG_ITEM_CONVERSION').toUpperCase() === 'TRUE',
  FEATURE_FLAG_OCB_READBACK: false,
  FEATURE_FLAG_NOTIFY_HITL_BUILD: false,
  FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION: false,
  FINISH_ORDER_BUTTON_DISABLE_INTERVAL: process.env
    .REACT_APP_FINISH_ORDER_BUTTON_DISABLE_INTERVAL
    ? parseInt(process.env.REACT_APP_FINISH_ORDER_BUTTON_DISABLE_INTERVAL)
    : 0, // 0 milliseconds default (disabled by default)
  FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES: false,
  FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE: false,
  FEATURE_FLAG_SUPPRESS_UPSELL: false,
  FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE: false,
  FEATURE_FLAG_UNRESOLVED_ITEMS: false,
  FEATURE_FLAG_HITL_3_0: false,
  FEATURE_FLAG_AGENT_CART: false,
  FEATURE_FLAG_HITL_EXPRESS_TIMER: false,
  HITL_EXPRESS_TIMER_MAX_SECONDS: 0,
  AGENT_RESPONSE_TIME_LIMIT_SECONDS: DEFAULT_AGENT_RESPONSE_TIME_LIMIT_SECONDS,
  FEATURE_FLAG_MENU_MODULE_ERROR_UI: false,
  FEATURE_FLAG_ORDER_CHECK_RETRY: false,
  ORDER_CHECK_TIMEOUT_SECONDS: DEFAULT_ORDER_CHECK_TIMEOUT_SECONDS,
  FEATURE_FLAG_MASK_ORDER_PENDING_ERROR: false,
  FEATURE_FLAG_SECONDARY_ASR_ENABLED: false,
  FEATURE_FLAG_ISSUE_REPORTING: false,
};

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    update: (state, action: PayloadAction<Partial<ConfigState>>) => {
      //This reducer is really not needed as environment variables which are passed are not changed during runtime
      Object.assign(state, action.payload);
    },
  },
});

export const { update } = configSlice.actions;
export default configSlice.reducer;
